/**
 * Session Initializer Component
 *
 * Initializes PostHog session tracking when the app loads
 * Captures entry metadata (wallet provider, referrer, etc.)
 * and sets up user properties for the session
 *
 * Also handles user identification when profile data is available
 */

import { useSearch } from "@tanstack/react-router";
import { useEffect, useRef } from "react";
import { useAnalytics } from "../hooks/usePostHog";
import { useRampsStore } from "../stores/use-ramps-store";

export function SessionInitializer() {
	const { trackSessionStart, trackExit, identifyUser } = useAnalytics();
	const search = useSearch({ strict: false });
	const store = useRampsStore();
	const hasInitialized = useRef(false);
	const hasIdentified = useRef(false);

	// Track session start
	useEffect(() => {
		// Only initialize once per session
		if (!hasInitialized.current) {
			hasInitialized.current = true;

			// Track session start with entry metadata
			trackSessionStart({});

			// Track session abandonment on page unload
			const handleBeforeUnload = () => {
				// Calculate basic completion metrics
				const urlParams = new URLSearchParams(window.location.search);
				const currentStep = urlParams.get("step") || "calculator";

				// Map steps to completion percentage
				const stepProgress: Record<string, number> = {
					calculator: 10,
					phone: 25,
					name: 40,
					address: 50,
					"date-of-birth": 60,
					identification: 70,
					"digital-offramp-info": 75,
					"additional-info": 80,
					review: 90,
					status: 100,
				};

				const completionPercentage = stepProgress[currentStep] || 0;

				// Only track abandonment if not on completion page
				if (currentStep !== "status") {
					trackExit("abandoned", {
						exit_reason: "browser_close_or_navigation",
						completion_percentage: completionPercentage,
						pages_visited: getVisitedPagesCount(),
					});
				}
			};

			// Listen for page unload
			window.addEventListener("beforeunload", handleBeforeUnload);

			return () => {
				window.removeEventListener("beforeunload", handleBeforeUnload);
			};
		}
	}, [trackSessionStart, trackExit]);

	// Identify user when profile or phone data is available
	useEffect(() => {
		const searchParams = search as Record<string, unknown>;
		const walletProvider =
			(searchParams?.wallet_provider as string) || "unknown";
		const operation =
			(searchParams?.operation as "withdraw" | "deposit") || "withdraw";

		// Determine if user has digital profile
		const hasDigitalProfile = !!store.digitalProfileId;
		const isReturningUser =
			hasDigitalProfile &&
			(store.walletLookupCompleted || store.phoneLookupCompleted);
		const isNewProfileCreated = store.profileCreationCompleted;

		// Try to identify user with available data
		if (!hasIdentified.current) {
			// PRIORITY 1: Use digital profile ID if available (best identifier)
			if (store.digitalProfileId) {
				identifyUser(`profile_${store.digitalProfileId}`, {
					// Profile identification
					digital_profile_id: store.digitalProfileId,
					has_digital_profile: true,
					is_returning_user: isReturningUser,
					profile_created_this_session: isNewProfileCreated,

					// Transaction context
					transaction_id: store.transactionId,
					wallet_address: store.walletAddress,
					wallet_provider: walletProvider,
					operation: operation,

					// User data
					phone_number: store.phone,
					email: store.email,
					name: `${store.firstName} ${store.lastName}`.trim() || undefined,

					// Preferences
					language: (searchParams?.lang as string) || "en",
					api_version: store.apiVersion || "v3",

					// Journey tracking
					wallet_lookup_completed: store.walletLookupCompleted,
					phone_lookup_completed: store.phoneLookupCompleted,
				});
				hasIdentified.current = true;
			}
			// PRIORITY 2: Use transaction ID if available but no profile yet
			else if (store.transactionId) {
				identifyUser(`tx_${store.transactionId}`, {
					transaction_id: store.transactionId,
					has_digital_profile: false,
					is_returning_user: false,
					wallet_provider: walletProvider,
					operation: operation,
					language: (searchParams?.lang as string) || "en",
					api_version: store.apiVersion || "v3",
				});
				hasIdentified.current = true;
			}
			// PRIORITY 3: Fallback to phone/email-based identification
			else if (store.phone || store.email) {
				const userId = store.phone
					? `phone_${store.phone}`
					: store.email
						? `email_${store.email}`
						: `session_${Date.now()}`;

				identifyUser(userId, {
					phone_number: store.phone,
					email: store.email,
					has_digital_profile: false,
					wallet_provider: walletProvider,
					operation: operation,
					language: (searchParams?.lang as string) || "en",
					api_version: store.apiVersion || "v3",
				});
				hasIdentified.current = true;
			}
		}
		// Update user properties when digital profile is found/created
		else if (store.digitalProfileId && hasIdentified.current) {
			// Re-identify with profile ID and update properties
			identifyUser(`profile_${store.digitalProfileId}`, {
				digital_profile_id: store.digitalProfileId,
				has_digital_profile: true,
				is_returning_user: isReturningUser,
				profile_created_this_session: isNewProfileCreated,
				transaction_id: store.transactionId,
				wallet_address: store.walletAddress,
				wallet_provider: walletProvider,
				phone_number: store.phone,
				email: store.email,
				wallet_lookup_completed: store.walletLookupCompleted,
				phone_lookup_completed: store.phoneLookupCompleted,
			});
		}
	}, [
		store.digitalProfileId,
		store.transactionId,
		store.phone,
		store.email,
		store.firstName,
		store.lastName,
		store.walletAddress,
		store.apiVersion,
		store.profileCreationCompleted,
		store.walletLookupCompleted,
		store.phoneLookupCompleted,
		search,
		identifyUser,
	]);

	// This is a tracking component - renders nothing
	return null;
}

/**
 * Helper to estimate pages visited based on session storage
 */
function getVisitedPagesCount(): number {
	try {
		const visited = sessionStorage.getItem("ramps_pages_visited");
		return visited ? JSON.parse(visited).length : 1;
	} catch {
		return 1;
	}
}
